import { TextField } from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { formatInternationalAddressDisplay } from './workbookFieldUtils'

interface GooglePlaceResult {
    address_components?: Array<{
        long_name: string
        short_name: string
        types: string[]
    }>
    formatted_address?: string
    geometry?: {
        location?: {
            lat: () => number
            lng: () => number
        }
    }
    place_id?: string
}

interface GoogleAutocomplete {
    addListener: (event: string, handler: () => void) => void
    getPlace: () => GooglePlaceResult
}

interface GoogleMapsPlaces {
    Autocomplete: new (
        input: HTMLInputElement,
        options?: { types?: string[]; componentRestrictions?: { country: string | string[] } },
    ) => GoogleAutocomplete
}

interface GoogleMapsWindow extends Window {
    google?: {
        maps?: {
            places?: GoogleMapsPlaces
            event?: {
                clearInstanceListeners: (instance: GoogleAutocomplete) => void
            }
        }
    }
}

export interface InternationalAddressPayload {
    id?: number
    country_code: string
    address_line1: string
    address_line2?: string
    locality: string
    administrative_area?: string
    administrative_area_code?: string
    postal_code?: string
    formatted_address?: string
    place_id?: string
    latitude?: number | null
    longitude?: number | null
    source?: 'places' | 'manual' | 'import'
}

const GOOGLE_MAPS_SCRIPT_ID = 'ezos-google-maps-places'

function loadGooglePlacesScript(apiKey: string): Promise<void> {
    const win = window as GoogleMapsWindow
    if (win.google?.maps?.places) {
        return Promise.resolve()
    }

    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID)
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener('load', () => resolve())
            existing.addEventListener('error', () =>
                reject(new Error('Failed to load Google Maps')),
            )
        })
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.id = GOOGLE_MAPS_SCRIPT_ID
        script.async = true
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load Google Maps'))
        document.head.appendChild(script)
    })
}

function parsePlaceToPayload(place: GooglePlaceResult): InternationalAddressPayload {
    const components = place.address_components ?? []
    const find = (...types: string[]) =>
        components.find((c) => types.some((t) => c.types.includes(t)))

    const streetNumber = find('street_number')?.long_name ?? ''
    const route = find('route')?.long_name ?? ''
    const line1 = `${streetNumber} ${route}`.trim()
    const locality =
        find('locality')?.long_name ||
        find('postal_town')?.long_name ||
        find('sublocality_level_1')?.long_name ||
        ''
    const admin = find('administrative_area_level_1')
    const country = find('country')
    const postal = find('postal_code')?.long_name ?? ''
    const countryCode = (country?.short_name || 'US').toUpperCase()
    const adminShort = admin?.short_name || ''
    const adminCode =
        countryCode && adminShort && adminShort.length <= 3
            ? `${countryCode}-${adminShort}`
            : ''

    let latitude: number | null = null
    let longitude: number | null = null
    try {
        latitude = place.geometry?.location?.lat() ?? null
        longitude = place.geometry?.location?.lng() ?? null
    } catch {
        latitude = null
        longitude = null
    }

    return {
        country_code: countryCode,
        address_line1: line1 || place.formatted_address || '',
        address_line2: '',
        locality: locality || 'Unknown',
        administrative_area: adminShort || admin?.long_name || '',
        administrative_area_code: adminCode,
        postal_code: postal,
        formatted_address: place.formatted_address || '',
        place_id: place.place_id || '',
        latitude,
        longitude,
        source: 'places',
    }
}

interface WorkbookAddressFieldProps {
    /** Workbook field_id — answers[fieldId] becomes InternationalAddress.id */
    fieldId: string
    label?: string
    /** Existing resolved address from application.resolved_addresses */
    resolvedAddress?: InternationalAddressPayload | null
    /** Legacy component answers (current_address_*) — display fallback only */
    legacyAnswers?: Record<string, unknown>
    /**
     * When true (virtual current_address), also write current_address_* components
     * so identity-phase completion keeps working until the template migrates.
     */
    alsoWriteLegacyComponents?: boolean
    required?: boolean
    onAnswersChange: (patch: Record<string, unknown>) => void
}

function legacyComponentPatch(
    payload: InternationalAddressPayload,
): Record<string, unknown> {
    return {
        current_address_line1: payload.address_line1,
        current_address_city: payload.locality,
        current_address_state: (payload.administrative_area || '').slice(0, 2),
        current_address_zip: payload.postal_code || '',
    }
}

export const WorkbookAddressField = ({
    fieldId,
    label = 'Address',
    resolvedAddress,
    legacyAnswers,
    alsoWriteLegacyComponents = false,
    required,
    onAnswersChange,
}: WorkbookAddressFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const autocompleteRef = useRef<GoogleAutocomplete | null>(null)
    const onAnswersChangeRef = useRef(onAnswersChange)
    const [displayValue, setDisplayValue] = useState(() =>
        formatInternationalAddressDisplay(resolvedAddress, legacyAnswers),
    )
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

    useEffect(() => {
        onAnswersChangeRef.current = onAnswersChange
    }, [onAnswersChange])

    useEffect(() => {
        setDisplayValue(
            formatInternationalAddressDisplay(resolvedAddress, legacyAnswers),
        )
    }, [resolvedAddress, legacyAnswers])

    useEffect(() => {
        if (!apiKey || !inputRef.current) return undefined

        let cancelled = false

        loadGooglePlacesScript(apiKey)
            .then(() => {
                if (cancelled || !inputRef.current) return
                const win = window as GoogleMapsWindow
                const places = win.google?.maps?.places
                if (!places) return

                // No country restriction — InternationalAddress supports worldwide.
                const autocomplete = new places.Autocomplete(inputRef.current, {
                    types: ['address'],
                })
                autocompleteRef.current = autocomplete

                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace()
                    const payload = parsePlaceToPayload(place)
                    const formatted =
                        payload.formatted_address ||
                        formatInternationalAddressDisplay(payload)
                    setDisplayValue(formatted)
                    const patch: Record<string, unknown> = { [fieldId]: payload }
                    if (alsoWriteLegacyComponents) {
                        Object.assign(patch, legacyComponentPatch(payload))
                    }
                    onAnswersChangeRef.current(patch)
                })
            })
            .catch(() => {
                // Fall back to plain text entry when Places cannot load.
            })

        return () => {
            cancelled = true
            const win = window as GoogleMapsWindow
            if (autocompleteRef.current && win.google?.maps?.event) {
                win.google.maps.event.clearInstanceListeners(autocompleteRef.current)
            }
            autocompleteRef.current = null
        }
    }, [apiKey, fieldId, alsoWriteLegacyComponents])

    return (
        <TextField
            inputRef={inputRef}
            label={label}
            value={displayValue}
            onChange={(e) => setDisplayValue(e.target.value)}
            onBlur={() => {
                const saved = formatInternationalAddressDisplay(
                    resolvedAddress,
                    legacyAnswers,
                )
                if (displayValue.trim() !== saved && displayValue.trim()) {
                    const payload: InternationalAddressPayload = {
                        country_code: resolvedAddress?.country_code || 'US',
                        address_line1: displayValue.trim(),
                        locality: resolvedAddress?.locality || 'Unknown',
                        administrative_area:
                            resolvedAddress?.administrative_area || '',
                        postal_code: resolvedAddress?.postal_code || '',
                        formatted_address: displayValue.trim(),
                        source: 'manual',
                    }
                    const patch: Record<string, unknown> = { [fieldId]: payload }
                    if (alsoWriteLegacyComponents) {
                        Object.assign(patch, legacyComponentPatch(payload))
                    }
                    onAnswersChangeRef.current(patch)
                }
            }}
            required={required}
            fullWidth
            helperText={
                apiKey
                    ? 'Start typing to search for your address'
                    : 'Enter your full street address'
            }
            sx={{
                '& .MuiInputLabel-root': { color: '#8b949e' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#46b083' },
                '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(139, 148, 158, 0.4)' },
                    '&:hover fieldset': { borderColor: 'rgba(139, 148, 158, 0.7)' },
                    '&.Mui-focused fieldset': { borderColor: '#46b083' },
                },
                '& .MuiFormHelperText-root': { color: '#8b949e' },
            }}
        />
    )
}
